import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BlobField from '../components/BlobField';
import LogoMark from '../components/LogoMark';
import LandingTrending from '../components/LandingTrending';
import api, { resolveAvatarUrl } from '../api';

/**
 * Public landing page — rendered at `/` when the visitor is not authenticated.
 * Authenticated users are redirected to /home by the route in App.js before
 * this component ever mounts.
 *
 * Layout:
 *   1. Header with logo + Sign in
 *   2. Hero: headline + subhead + two CTAs
 *   3. Drifting clan cards behind the hero — click a card → creator profile
 *   4. "How it works" 3-step strip
 *   5. Live stats footer strip
 */
export default function Landing() {
  const navigate = useNavigate();
  const [clans, setClans] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/public/discover?limit=15')
      .then(({ data }) => setClans(data.clans || []))
      .catch(() => setClans([]));
    api.get('/creators/leaderboard?limit=1')
      .then(({ data }) => setStats(data.stats || null))
      .catch(() => {});
  }, []);

  return (
    <div className="landing-page" data-testid="landing-page">
      <BlobField />

      <header className="landing-header">
        <div className="landing-brand" data-testid="landing-brand">
          <LogoMark size={36} />
          <span>clanster</span>
        </div>
        <Link to="/login" className="landing-signin" data-testid="landing-signin">
          Sign in
        </Link>
      </header>

      <section className="landing-hero">
        <FloatingCards clans={clans} />

        <div className="landing-hero-inner">
          <h1 className="landing-headline">
            Own a Bit. <br />Join the Clan.
          </h1>
          <p className="landing-sub">
            Communities you can actually buy into. Get in the room. Trade or hold — your call.
          </p>
          <div className="landing-cta-row">
            <button
              className="landing-cta primary"
              onClick={() => navigate('/onboarding')}
              data-testid="landing-cta-invite"
            >
              I have an invite code →
            </button>
            <button
              className="landing-cta ghost"
              onClick={() => navigate('/onboarding?waitlist=1')}
              data-testid="landing-cta-waitlist"
            >
              Join the waitlist
            </button>
          </div>
        </div>
      </section>

      <section className="landing-how" data-testid="landing-how">
        <h2 className="landing-h2">How it works</h2>
        <div className="landing-steps">
          <div className="landing-step">
            <div className="landing-step-n">1</div>
            <div className="landing-step-t">Get invited</div>
            <div className="landing-step-b">A friend shares their referral link — you sign up in 30 seconds.</div>
          </div>
          <div className="landing-step">
            <div className="landing-step-n">2</div>
            <div className="landing-step-t">Buy Bits of Clans you love</div>
            <div className="landing-step-b">More people buying pushes the price higher, while selling lowers it. You get instant access to the private feed.</div>
          </div>
          <div className="landing-step">
            <div className="landing-step-n">3</div>
            <div className="landing-step-t">Trade or hold</div>
            <div className="landing-step-b">Trade your bits to book profits or hold minimum to enjoy clan benefits.</div>
          </div>
        </div>

        <a
          href="https://t.me/+aGYr33RHnu9kMTQ1"
          target="_blank"
          rel="noopener noreferrer"
          className="landing-telegram"
          data-testid="landing-telegram"
          aria-label="Join our Telegram community"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.24 3.64 11.95c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16l-1.99 1.93c-.23.23-.42.42-.83.42z"/>
          </svg>
          <span>Join our community on Telegram</span>
        </a>
      </section>

      {/* GL4 fan-side on Landing: live momentum leaderboard as social proof.
          Hides itself entirely when no clans have earned momentum yet — never
          renders an empty state on a marketing page. */}
      <LandingTrending />

      <footer className="landing-foot">
        {stats && stats.total_clans ? (
          <span data-testid="landing-stats">
            {stats.total_clans.toLocaleString()} clans · {(stats.total_bits_minted || 0).toLocaleString()} bits minted
          </span>
        ) : (
          <span>Built for creators + fans · designed by-and-for the community</span>
        )}
        <span className="landing-foot-links">
          <Link to="/help">Help</Link>
          <Link to="/legal">Legal</Link>
        </span>
      </footer>
    </div>
  );
}

/**
 * Physics-driven floating clan cards.
 *
 *  - Cards drift with velocity, bouncing off viewport edges and the center
 *    hero card so they NEVER go behind the CTA. The center card is treated
 *    as a solid wall.
 *  - Card ↔ card collisions swap velocities (AABB check) so cards nudge
 *    each other organically.
 *  - Every wall/card-hit on the center card fires a subtle shake animation
 *    on the hero via a CSS class flip. Between bumps the hero has a slow
 *    idle bob so it never feels frozen.
 *  - Cards spawn one every ~1.5s up to a cap of 6 (4 on mobile).
 *  - Positions live in refs and mutate DOM directly via requestAnimationFrame
 *    — no React re-renders during motion.
 */
const CARD_WIDTH_DESKTOP = 310;
const CARD_HEIGHT_DESKTOP = 92;
const CARD_WIDTH_MOBILE = 240;
const CARD_HEIGHT_MOBILE = 82;
const MAX_CARDS_DESKTOP = 6;
const MAX_CARDS_MOBILE = 4;
const SPAWN_INTERVAL_MS = 1600;
const BASE_SPEED = 14;                  // px/second — gentle drift
const SPEED_VARIANCE = 6;               // ± px/s per card
const STUCK_WINDOW_MS = 3200;           // observation window
const STUCK_DIST_PX = 26;                // min displacement over the window
const FADE_MS = 1600;                    // slow, elegant fade-out (matches fade-in)

function FloatingCards({ clans }) {
  const containerRef = useRef(null);
  const heroRef = useRef(null);
  const nodesRef = useRef(new Map());     // id -> DOM node
  const stateRef = useRef([]);            // physics state per card
  const bumpTimerRef = useRef(null);
  const [renderedCards, setRenderedCards] = useState([]); // just for React key/mount

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const cardW = isMobile ? CARD_WIDTH_MOBILE : CARD_WIDTH_DESKTOP;
  const cardH = isMobile ? CARD_HEIGHT_MOBILE : CARD_HEIGHT_DESKTOP;
  // Cap card count to available DP-enabled clans so we never duplicate
  // (e.g. if only 1 creator has a DP we show 1 card, not 6 copies).
  const desktopMax = isMobile ? MAX_CARDS_MOBILE : MAX_CARDS_DESKTOP;
  const maxCards = Math.max(1, Math.min(desktopMax, clans.length));

  // Find the hero element once — it's a sibling inside the same section.
  useEffect(() => {
    heroRef.current = document.querySelector('.landing-hero-inner') || null;
  }, []);

  // Spawn cards over time (start with 2, add 1 every SPAWN_INTERVAL_MS)
  useEffect(() => {
    if (clans.length === 0) return undefined;

    const spawnOne = () => {
      const container = containerRef.current;
      if (!container) return;
      const box = container.getBoundingClientRect();
      if (stateRef.current.length >= maxCards) return;
      const usedHandles = new Set(stateRef.current.map((s) => s.clan.handle));
      const clan = pickClan(clans, usedHandles);
      if (!clan) return;

      // Enter from a random side, aimed inward at a gentle angle
      const side = ['top', 'right', 'bottom', 'left'][Math.floor(Math.random() * 4)];
      const speed = BASE_SPEED + (Math.random() - 0.5) * 2 * SPEED_VARIANCE;
      let x; let y; let heading;
      if (side === 'left') {
        x = -cardW - 20;
        y = 40 + Math.random() * Math.max(1, box.height - cardH - 80);
        heading = (Math.random() - 0.5) * 0.8;                     // ~0 rad (right)
      } else if (side === 'right') {
        x = box.width + 20;
        y = 40 + Math.random() * Math.max(1, box.height - cardH - 80);
        heading = Math.PI + (Math.random() - 0.5) * 0.8;           // ~π (left)
      } else if (side === 'top') {
        x = 40 + Math.random() * Math.max(1, box.width - cardW - 80);
        y = -cardH - 20;
        heading = Math.PI / 2 + (Math.random() - 0.5) * 0.8;       // ~π/2 (down)
      } else {
        x = 40 + Math.random() * Math.max(1, box.width - cardW - 80);
        y = box.height + 20;
        heading = -Math.PI / 2 + (Math.random() - 0.5) * 0.8;      // ~-π/2 (up)
      }

      const id = `${clan.handle}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      stateRef.current.push({
        id, clan,
        x, y,
        speed, heading,
        // Perlin-ish angular drift so each card curves independently
        noisePhase: Math.random() * Math.PI * 2,
        noiseFreq: 0.35 + Math.random() * 0.35,                     // rad/s
        noiseAmp:  0.55 + Math.random() * 0.35,                     // rad
        // Stuck-detection accounting
        lastCheckX: x, lastCheckY: y, lastCheckAt: performance.now(),
        // Fade state (for graceful respawn on stuck)
        fading: false,
      });
      setRenderedCards((prev) => [...prev, { id, clan }]);
    };

    // Start with 2 already visible so the page isn't empty on load
    for (let i = 0; i < 2; i++) spawnOne();

    const timer = setInterval(spawnOne, SPAWN_INTERVAL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clans]);

  // Physics loop — mutates DOM directly (no setState per frame)
  useEffect(() => {
    let last = performance.now();
    let raf;

    const heroBump = () => {
      if (!heroRef.current) return;
      heroRef.current.classList.remove('landing-hero-bumped');
      // Force reflow so the animation retriggers even on rapid hits
      void heroRef.current.offsetWidth;
      heroRef.current.classList.add('landing-hero-bumped');
      clearTimeout(bumpTimerRef.current);
      bumpTimerRef.current = setTimeout(() => {
        heroRef.current?.classList.remove('landing-hero-bumped');
      }, 480);
    };

    const step = (now) => {
      const dt = Math.min(0.06, (now - last) / 1000); // clamp to 60ms
      last = now;
      const container = containerRef.current;
      if (!container) { raf = requestAnimationFrame(step); return; }
      const box = container.getBoundingClientRect();
      const containerLeft = box.left;
      const containerTop = box.top;

      // Compute hero's rectangle relative to container (once per frame)
      let heroRect = null;
      if (heroRef.current) {
        const r = heroRef.current.getBoundingClientRect();
        heroRect = {
          left: r.left - containerLeft - 8,
          top: r.top - containerTop - 8,
          right: r.right - containerLeft + 8,
          bottom: r.bottom - containerTop + 8,
        };
      }

      const nowSec = now / 1000;
      const cards = stateRef.current;
      for (let i = 0; i < cards.length; i++) {
        const c = cards[i];
        if (c.fading) continue;   // skip physics while fading out

        // Continuous, sine-based heading drift → smooth curved paths that
        // never repeat. Each card has its own phase / frequency / amplitude
        // so the swarm feels organic rather than choreographed.
        const drift = Math.sin(nowSec * c.noiseFreq + c.noisePhase) * c.noiseAmp * dt;
        c.heading += drift;

        const vx = Math.cos(c.heading) * c.speed;
        const vy = Math.sin(c.heading) * c.speed;
        c.x += vx * dt;
        c.y += vy * dt;

        // Viewport bounds — soft reflect the heading rather than hard flip
        // velocity. Also nudge the heading by a small random angle so cards
        // don't lock into a perfect back-and-forth path along an edge.
        const jitter = () => (Math.random() - 0.5) * 0.6;   // ± ~17°
        if (c.x < 0)                                        { c.x = 0;                        c.heading = Math.PI - c.heading + jitter(); }
        else if (c.x + cardW > box.width)                   { c.x = box.width - cardW;        c.heading = Math.PI - c.heading + jitter(); }
        if (c.y < 0)                                        { c.y = 0;                        c.heading = -c.heading + jitter(); }
        else if (c.y + cardH > box.height)                  { c.y = box.height - cardH;       c.heading = -c.heading + jitter(); }

        // Hero card wall — push out along the shortest axis, reflect heading
        if (heroRect &&
            c.x + cardW > heroRect.left &&
            c.x < heroRect.right &&
            c.y + cardH > heroRect.top &&
            c.y < heroRect.bottom) {
          const overlapLeft   = c.x + cardW - heroRect.left;
          const overlapRight  = heroRect.right - c.x;
          const overlapTop    = c.y + cardH - heroRect.top;
          const overlapBottom = heroRect.bottom - c.y;
          const minH = Math.min(overlapLeft, overlapRight);
          const minV = Math.min(overlapTop, overlapBottom);
          if (minH < minV) {
            if (overlapLeft < overlapRight) c.x = heroRect.left - cardW;
            else                             c.x = heroRect.right;
            c.heading = Math.PI - c.heading + jitter();
          } else {
            if (overlapTop < overlapBottom) c.y = heroRect.top - cardH;
            else                             c.y = heroRect.bottom;
            c.heading = -c.heading + jitter();
          }
          heroBump();
        }

        // Card ↔ card — reflect headings along the separation axis so they
        // pass by each other gracefully instead of clumping.
        for (let j = i + 1; j < cards.length; j++) {
          const o = cards[j];
          if (o.fading) continue;
          if (c.x + cardW > o.x && c.x < o.x + cardW &&
              c.y + cardH > o.y && c.y < o.y + cardH) {
            const dx = (c.x + cardW / 2) - (o.x + cardW / 2);
            const dy = (c.y + cardH / 2) - (o.y + cardH / 2);
            if (Math.abs(dx) > Math.abs(dy)) {
              c.heading = Math.PI - c.heading + jitter();
              o.heading = Math.PI - o.heading + jitter();
              if (dx > 0) { c.x += 3; o.x -= 3; } else { c.x -= 3; o.x += 3; }
            } else {
              c.heading = -c.heading + jitter();
              o.heading = -o.heading + jitter();
              if (dy > 0) { c.y += 3; o.y -= 3; } else { c.y -= 3; o.y += 3; }
            }
          }
        }

        // Stuck detection — if the card hasn't moved meaningfully in the
        // observation window, fade it out and let the spawner replace it
        // somewhere new. Prevents lingering in a corner or oscillating.
        if (now - c.lastCheckAt >= STUCK_WINDOW_MS) {
          const moved = Math.hypot(c.x - c.lastCheckX, c.y - c.lastCheckY);
          if (moved < STUCK_DIST_PX) {
            c.fading = true;
            const node = nodesRef.current.get(c.id);
            if (node) {
              node.style.transition = `opacity ${FADE_MS}ms ease-out`;
              node.style.opacity = '0';
            }
            setTimeout(() => {
              stateRef.current = stateRef.current.filter((s) => s.id !== c.id);
              setRenderedCards((prev) => prev.filter((r) => r.id !== c.id));
              nodesRef.current.delete(c.id);
            }, FADE_MS + 30);
          } else {
            c.lastCheckX = c.x; c.lastCheckY = c.y; c.lastCheckAt = now;
          }
        }

        const node = nodesRef.current.get(c.id);
        if (node && !c.fading) node.style.transform = `translate3d(${c.x}px, ${c.y}px, 0)`;
      }

      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [cardW, cardH]);

  const setNodeRef = (id) => (el) => {
    if (el) nodesRef.current.set(id, el);
    else nodesRef.current.delete(id);
  };

  return (
    <div className="landing-cards" ref={containerRef} aria-hidden="false">
      {renderedCards.map((s) => (
        <FloatingCard key={s.id} clan={s.clan} nodeRef={setNodeRef(s.id)} />
      ))}
    </div>
  );
}

function pickClan(clans, avoid) {
  if (!clans.length) return null;
  const pool = clans.filter((c) => !avoid.has(c.handle));
  const src = pool.length ? pool : clans;
  return src[Math.floor(Math.random() * src.length)];
}

function formatMC(n) {
  if (!n || n < 1) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
}

function FloatingCard({ clan, nodeRef }) {
  const avatarUrl = resolveAvatarUrl(clan.avatar);
  const [imgOk, setImgOk] = useState(!!avatarUrl);
  const change = Number(clan.change_24h || 0);
  const changeClass = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
  const changeSign = change > 0 ? '+' : '';
  return (
    <Link
      to={`/creator/${clan.handle}`}
      className="landing-card"
      ref={nodeRef}
      data-testid={`landing-card-${clan.handle}`}
    >
      <div
        className="landing-card-avatar"
        style={{ background: `linear-gradient(135deg, ${clan.gradient[0]}, ${clan.gradient[1]})` }}
        aria-hidden="true"
      >
        {avatarUrl && imgOk ? (
          <img
            src={avatarUrl}
            alt=""
            loading="lazy"
            onError={() => setImgOk(false)}
          />
        ) : (
          <span>{clan.initials}</span>
        )}
      </div>
      <div className="landing-card-body">
        <div className="landing-card-head">
          <span className="landing-card-handle">@{clan.handle}</span>
          <span className={`landing-card-change ${changeClass}`}>
            {changeSign}{change.toFixed(1)}%
          </span>
        </div>
        {clan.tagline && (
          <div className="landing-card-tagline" title={clan.tagline}>{clan.tagline}</div>
        )}
        <div className="landing-card-meta">
          <span className="landing-card-mc">MC {formatMC(clan.market_cap)}</span>
          <span className="landing-card-dot">·</span>
          <span className="landing-card-holders">{clan.holders_count || 0} holders</span>
        </div>
      </div>
    </Link>
  );
}
